import { type FC, type FormEvent, useState } from "react";
import {
  Alert,
  AppBar,
  Button,
  Card,
  CardContent,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { ProductsAutocomplete } from "@/partner/features/products";
import {
  PromocodeTypeType,
  type CreateMultiplePromocodesInputType,
} from "@/types/graphql/partner.generated";
import { PromocodeCampaignsAutocomplete } from "./PromocodeCampaignsAutocomplete";

const FORM_SPACING = 2;

const SELECTED_TOGGLE_SX = {
  "&.Mui-selected": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
  },
} as const;

interface PromocodesMultipleFormProps {
  loading: boolean;
  error?: string | null;
  onSubmit: (input: CreateMultiplePromocodesInputType) => void;
  onClose: () => void;
}

/**
 * Форма масс-генерации промокодов (référence: PromocodesForm.js). Осознанное
 * дублирование разметки с PromocodeForm (заголовок/Card/Grid) — брифом
 * задачи 8 отдельный shared-компонент не выносится (потребовал бы более пяти
 * пропов-дырок для расхождений между формами).
 *
 * Отличия от одиночной формы: без title/code/метрик; первое поле — count
 * (кол-во промокодов); кампания обязательна (required и на автокомплите, и
 * на поле новой кампании); limit/discount/amount всегда required — без
 * взаимоисключающей логики одиночной формы; submit всегда
 * common.actions.create.
 */
export const PromocodesMultipleForm: FC<PromocodesMultipleFormProps> = ({
  loading,
  error,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();

  const [count, setCount] = useState("1");
  const [productIds, setProductIds] = useState<string[]>([]);
  const [limit, setLimit] = useState("1");
  const [discount, setDiscount] = useState("0");
  const [amount, setAmount] = useState("0");
  const [type, setType] = useState<PromocodeTypeType>(
    PromocodeTypeType.DiscountType
  );
  const [promocodeCampaignId, setPromocodeCampaignId] = useState<string | null>(
    null
  );
  const [promocodeCampaignTitle, setPromocodeCampaignTitle] = useState("");
  const [createNewCampaign, setCreateNewCampaign] = useState(false);
  const [trialPeriod, setTrialPeriod] = useState("0");
  const [startAt, setStartAt] = useState<moment.Moment | null>(null);
  const [finishAt, setFinishAt] = useState<moment.Moment | null>(null);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({
      count: parseInt(count, 10),
      type,
      promocodeCampaignId: createNewCampaign ? null : promocodeCampaignId,
      promocodeCampaignTitle: createNewCampaign ? promocodeCampaignTitle : null,
      productIds,
      limit: limit === "" ? null : parseInt(limit, 10),
      discount: discount === "" ? null : parseFloat(discount),
      amount: amount === "" ? null : parseFloat(amount),
      trialPeriod: trialPeriod === "" ? null : parseInt(trialPeriod, 10),
      startAt: startAt?.toDate() ?? null,
      finishAt: finishAt?.toDate() ?? null,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            size="large"
            onClick={onClose}
            sx={{ mr: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {t("promocodes.dialog.createMultipleTitle")}
          </Typography>
          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            disableElevation
            sx={{ ml: 2 }}
          >
            {t("common.actions.create")}
          </Button>
        </Toolbar>
        <Divider />
      </AppBar>

      <DialogContent sx={{ p: 1 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Card elevation={0}>
              <CardContent sx={{ pt: 3 }}>
                <Grid container spacing={FORM_SPACING}>
                  {error && (
                    <Grid size={12}>
                      <Alert severity="error">{error}</Alert>
                    </Grid>
                  )}

                  <Grid size={12}>
                    <TextField
                      required
                      fullWidth
                      type="number"
                      label={t("promocodes.form.count")}
                      value={count}
                      onChange={(event) => setCount(event.target.value)}
                      slotProps={{ htmlInput: { min: 1 } }}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Stack spacing={1}>
                      <PromocodeCampaignsAutocomplete
                        multiple={false}
                        disabled={createNewCampaign}
                        required
                        label={t("promocodes.form.campaign")}
                        value={promocodeCampaignId ? [promocodeCampaignId] : []}
                        onChange={(ids) =>
                          setPromocodeCampaignId(ids[0] ?? null)
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={createNewCampaign}
                            onChange={(event) =>
                              setCreateNewCampaign(event.target.checked)
                            }
                          />
                        }
                        label={t("promocodes.form.newCampaign")}
                      />
                      {createNewCampaign && (
                        <TextField
                          required
                          autoFocus
                          fullWidth
                          label={t("promocodes.form.newCampaignTitle")}
                          value={promocodeCampaignTitle}
                          onChange={(event) =>
                            setPromocodeCampaignTitle(event.target.value)
                          }
                        />
                      )}
                    </Stack>
                  </Grid>

                  <Grid size={12}>
                    <ToggleButtonGroup
                      exclusive
                      value={type}
                      onChange={(_event, value: PromocodeTypeType | null) => {
                        if (value) setType(value);
                      }}
                    >
                      <ToggleButton
                        value={PromocodeTypeType.DiscountType}
                        sx={SELECTED_TOGGLE_SX}
                      >
                        {t("promocodes.form.typeDiscount")}
                      </ToggleButton>
                      <ToggleButton
                        value={PromocodeTypeType.TrialType}
                        sx={SELECTED_TOGGLE_SX}
                      >
                        {t("promocodes.form.typeTrial")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mt: 0.75 }}
                    >
                      {type === PromocodeTypeType.DiscountType
                        ? t("promocodes.form.typeDiscountNotice")
                        : t("promocodes.form.typeTrialNotice")}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      required
                      fullWidth
                      type="number"
                      label={t("promocodes.form.limit")}
                      value={limit}
                      onChange={(event) => setLimit(event.target.value)}
                      slotProps={{ htmlInput: { min: 0 } }}
                    />
                  </Grid>

                  {type === PromocodeTypeType.DiscountType ? (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          required
                          type="number"
                          label={t("promocodes.form.discount")}
                          value={discount}
                          onChange={(event) => {
                            const raw = event.target.value;
                            if (raw === "") {
                              setDiscount("");
                              return;
                            }
                            setDiscount(
                              String(Math.max(0, Math.min(100, Number(raw))))
                            );
                          }}
                          slotProps={{ htmlInput: { min: 0, max: 100 } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          required
                          type="number"
                          label={t("promocodes.form.amount")}
                          value={amount}
                          onChange={(event) => setAmount(event.target.value)}
                          slotProps={{ htmlInput: { min: 0 } }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        required
                        type="number"
                        label={t("promocodes.form.trialPeriod")}
                        value={trialPeriod}
                        onChange={(event) => setTrialPeriod(event.target.value)}
                        slotProps={{
                          htmlInput: { min: 0, max: 100 },
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                {t("promocodes.form.days", {
                                  count: Number(trialPeriod) || 0,
                                })}
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid>
                  )}

                  <Grid size={12}>
                    <ProductsAutocomplete
                      label={t("promocodes.form.products")}
                      value={productIds}
                      onChange={setProductIds}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Typography color="textSecondary">
                      {t("promocodes.form.period")}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DateTimePicker
                      label={t("promocodes.form.startAt")}
                      value={startAt}
                      onChange={setStartAt}
                      slotProps={{
                        textField: { required: true, fullWidth: true },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DateTimePicker
                      label={t("promocodes.form.finishAt")}
                      value={finishAt}
                      onChange={setFinishAt}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </form>
  );
};
